
import { Activity, ActivityCategory, SearchMetadata, Coordinates } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getAllActivitiesForSearch } from '@/services/activityService';
import { geocodeCity } from './geocodingService';
import { calculateDistance } from '@/lib/utils';
import { SEARCH_CONFIG } from '@/config/searchConfig';

// Fonction pour filtrer les activités par texte (activity + venue description)
const filterBySearchText = (activities: Activity[], searchText: string): Activity[] => {
  const trimmed = searchText?.trim();
  if (!trimmed) return activities;

  const lowerSearchText = trimmed.toLowerCase();
  return activities.filter((activity) => {
    const nameMatch = activity.name?.toLowerCase().includes(lowerSearchText);
    const descMatch = activity.shortDescription?.toLowerCase().includes(lowerSearchText);
    const venueDescMatch =
      activity.venueShortDescription?.toLowerCase().includes(lowerSearchText) ?? false;
    return nameMatch || descMatch || venueDescMatch;
  });
};

// Fonction pour filtrer les activités par localisation avec proximité
const filterByLocationWithProximity = async (
  activities: Activity[],
  location: string
): Promise<{ activities: Activity[]; metadata: SearchMetadata }> => {
  if (!location) {
    return {
      activities,
      metadata: {
        radiusUsed: 0,
        searchCoordinates: null,
        expandedSearch: false,
      },
    };
  }

  const lowerLocation = location.toLowerCase();

  // First, try exact match (current behavior)
  const exactMatches = activities.filter(
    (activity) =>
      activity.city.toLowerCase().includes(lowerLocation) ||
      activity.address.toLowerCase().includes(lowerLocation) ||
      activity.postalCode.includes(lowerLocation)
  );

  if (exactMatches.length > 0) {
    // Return exact matches without distance calculation
    return {
      activities: exactMatches,
      metadata: {
        radiusUsed: 0,
        searchCoordinates: null,
        expandedSearch: false,
        searchLocation: location,
      },
    };
  }

  // No exact matches, try proximity search
  const searchCoordinates = await geocodeCity(location);

  if (!searchCoordinates) {
    // Could not geocode the location, return empty
    return {
      activities: [],
      metadata: {
        radiusUsed: 0,
        searchCoordinates: null,
        expandedSearch: false,
        searchLocation: location,
      },
    };
  }

  // Try progressive radius expansion
  const radiusSteps = SEARCH_CONFIG.AUTO_EXPAND_RADIUS
    ? SEARCH_CONFIG.RADIUS_EXPANSION_STEPS
    : [SEARCH_CONFIG.DEFAULT_RADIUS_KM];

  for (const radius of radiusSteps) {
    const activitiesWithDistance = activities
      .filter((activity) => activity.latitude && activity.longitude)
      .map((activity) => {
        const distance = calculateDistance(
          searchCoordinates.latitude,
          searchCoordinates.longitude,
          activity.latitude!,
          activity.longitude!
        );
        return {
          ...activity,
          distanceFromSearch: distance,
        };
      })
      .filter((activity) => activity.distanceFromSearch! <= radius)
      .sort((a, b) => a.distanceFromSearch! - b.distanceFromSearch!);

    if (activitiesWithDistance.length > 0) {
      return {
        activities: activitiesWithDistance,
        metadata: {
          radiusUsed: radius,
          searchCoordinates,
          expandedSearch: radius > SEARCH_CONFIG.DEFAULT_RADIUS_KM,
          searchLocation: location,
        },
      };
    }
  }

  // No results found even at max radius
  return {
    activities: [],
    metadata: {
      radiusUsed: SEARCH_CONFIG.MAX_RADIUS_KM,
      searchCoordinates,
      expandedSearch: true,
      searchLocation: location,
    },
  };
};

// Fonction pour filtrer les activités par catégorie
const filterByCategory = (activities: Activity[], category: ActivityCategory | ''): Activity[] => {
  if (!category) return activities;
  
  return activities.filter(activity => activity.category === category);
};

// Fonction principale de recherche
export const searchActivities = async (
  searchText: string = '',
  location: string = '',
  category: ActivityCategory | '' = ''
): Promise<{ activities: Activity[]; metadata: SearchMetadata }> => {
  // Fetch all activities with venue short_description for search
  const allActivities = await getAllActivitiesForSearch();
  let results = [...allActivities];
  let searchMetadata: SearchMetadata = {
    radiusUsed: 0,
    searchCoordinates: null,
    expandedSearch: false,
  };

  // Appliquer les filtres en séquence
  if (searchText) {
    results = filterBySearchText(results, searchText);
  }

  if (category) {
    results = filterByCategory(results, category);
  }

  // Apply location filter with proximity logic
  if (location) {
    const locationResult = await filterByLocationWithProximity(results, location);
    results = locationResult.activities;
    searchMetadata = locationResult.metadata;
  }

  return { activities: results, metadata: searchMetadata };
};
